import {useEffect, useRef} from 'react'
import type {TFunction} from 'i18next'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {LockGlyph} from '@/components/ui/icons'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {
  useDismissValidation,
  useReanalyze,
  type Evidence,
  type ValidationFinding,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {track} from '@/lib/telemetry'

const APP_OWNED_FINDINGS = new Set(['possible_historia_clinica', 'wrong_kind', 'illegible'])

function findingCopy(t: TFunction, finding: ValidationFinding): string {
  if (APP_OWNED_FINDINGS.has(finding.code)) return t(`validation.finding.${finding.code}`)
  return finding.detail_es ?? t('validation.finding.generic')
}

// Advisory only: never touches coverage or readiness.
export function ValidationState({item, businessId, editable}: {item: Evidence; businessId: string; editable: boolean}) {
  const {t} = useTranslation()
  const dismiss = useDismissValidation(businessId)
  const reanalyze = useReanalyze(businessId)
  const custodia = useCapabilities(businessId).sgsst_custodiar_salud
  const dismissed = item.validation_dismissed_at != null
  // El servidor no le manda lo que la IA leyó en un documento de salud a quien no lo custodia.
  const reservada = item.de_salud && item.validation_findings == null
  const acciona = editable && (!item.de_salud || custodia)

  const flaggedShown = item.validation_status === 'flagged' && !dismissed && !reservada
  const reportedRef = useRef<string | null>(null)
  useEffect(() => {
    if (flaggedShown && reportedRef.current !== item.id) {
      reportedRef.current = item.id
      track('ai_flagged_shown', {business_id: businessId, standard_code: item.standard_code})
    }
  }, [flaggedShown, item.id, item.standard_code, businessId])

  if (item.validation_status === 'pending')
    return (
      <p className="font-mono text-[9px] tracking-wider text-muted-foreground uppercase" data-testid="ai-pending">
        {t('validation.pending')}
      </p>
    )

  if (item.validation_status === 'ok')
    return (
      <p className="font-mono text-[9px] tracking-wider text-primary" data-testid="ai-ok">
        {t('validation.ok')}
      </p>
    )

  if (item.validation_status === 'skipped')
    return (
      <p className="font-mono text-[9px] tracking-wider text-muted-foreground uppercase" data-testid="ai-skipped">
        {t('validation.skipped')}
      </p>
    )

  if (item.validation_status === 'error')
    return (
      <div className="flex items-center gap-2" data-testid="ai-error">
        <span className="font-mono text-[9px] tracking-wider text-muted-foreground uppercase">
          {t('validation.error')}
        </span>
        {acciona && (
          <button
            type="button"
            className="text-[10px] font-medium text-primary hover:underline"
            onClick={() => reanalyze.mutate(item.id)}>
            {t('validation.retry')}
          </button>
        )}
      </div>
    )

  if (item.validation_status === 'flagged' && !dismissed && reservada)
    return (
      <p className="flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="ai-reservada">
        <LockGlyph />
        {t('validation.reservada')}
      </p>
    )

  if (item.validation_status === 'flagged' && !dismissed)
    return (
      <div className="tone-amber space-y-1 rounded px-1.5 py-1 text-[10px]" data-testid="ai-flagged">
        {(item.validation_findings ?? []).map((finding) => (
          <p key={finding.code}>{findingCopy(t, finding)}</p>
        ))}
        {acciona && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[10px]"
            data-testid="ai-dismiss"
            onClick={() => dismiss.mutate(item.id)}>
            {t('validation.dismiss')}
          </Button>
        )}
      </div>
    )

  return null
}
